import "./TaskPanelAttachments.css";

import { PlusIcon } from "../../../../assets";
import { convertDateToStringDDMMYYYY } from "../../../../utils";
import { ObjectAttachment } from "../../../../utils/hooks/useObjectAttachments";
import { Button, DocumentListItem, DocumentListVariant, FormLabel } from "../../../../widget";

export interface TaskPanelAttachmentsProps {
  attachments: ObjectAttachment[];
  isLoading: boolean;
  /** View-tier users see the list without the per-item remove action. */
  canRemove: boolean;
  /**
   * Whether the add control is offered. Narrower than `canRemove`: uploading depends on the host being able
   * to serve the upload modal, while removing only unlinks an existing document.
   */
  canAdd: boolean;
  variant: DocumentListVariant;
  onAddClick: () => void;
  onRemove: (documentUuid: string) => void;
  /*
    Downloads through the store's authenticated endpoint. Passed rather than left to the item's own url
    fallback, which cannot read a private document's file.
  */
  onDownload: (documentUuid: string) => void;
}

export const TaskPanelAttachments = ({
  attachments,
  isLoading,
  canRemove,
  canAdd,
  variant,
  onAddClick,
  onRemove,
  onDownload,
}: TaskPanelAttachmentsProps): JSX.Element => {
  return (
    <div className="TaskPanelAttachments">
      <FormLabel label="Attachments" />
      {attachments.length > 0 && (
        <div className="TaskPanelAttachmentsList">
          {attachments.map((attachment) => (
            <DocumentListItem
              key={attachment.documentUuid}
              documentUuid={attachment.documentUuid}
              fileName={attachment.fileName}
              /* Variant qualifies the type, as it does in the document tables ("Other — Site plan"). */
              documentType={
                attachment.variant
                  ? `${attachment.documentType} — ${attachment.variant}`
                  : (attachment.documentType ?? undefined)
              }
              uploadedAt={attachment.uploadedAt ? convertDateToStringDDMMYYYY(attachment.uploadedAt) : undefined}
              variant={variant}
              readOnly={!canRemove}
              onRemove={onRemove}
              /* No `url`: the item's url fallback opens the file directly, which a private document rejects. */
              onDownload={onDownload}
            />
          ))}
        </div>
      )}
      {/* An empty list with no add control still needs to say so, rather than showing only the label. */}
      {attachments.length === 0 && !isLoading && !canAdd && <p className="body1">No attachments</p>}
      {canAdd && (
        <div className="TaskPanelAttachmentsAdd">
          <Button
            text="Add attachment"
            icon={<PlusIcon className="TaskPanelAttachmentsAddIcon" />}
            iconPosition="left"
            variant="secondary"
            size="small"
            onClick={onAddClick}
            minWidth={false}
            hasNoPadding
          />
        </div>
      )}
    </div>
  );
};
